import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { setAccessToken } from "@/lib/api";
import * as authApi from "./api";
import type { LoginCredentials, UserSummary } from "./types";

interface AuthContextValue {
  user: UserSummary | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  can: (permission: string) => boolean;
  canAny: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserSummary | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // On mount the access token is always absent — it lives in memory and dies
  // with the page. The httpOnly refresh cookie is what survives, so we probe
  // /auth/me; apiFetch transparently refreshes on the 401. This is what makes
  // a page reload keep the session without ever storing a token in JS.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await authApi.fetchMe();
        if (!cancelled) setUser(me);
      } catch {
        if (!cancelled) {
          setUser(null);
          setAccessToken(null);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (credentials: LoginCredentials) => {
    const res = await authApi.login(credentials);
    setUser(res.user);
  }, []);

  const logout = useCallback(async () => {
    await authApi.logout();
    setUser(null);
  }, []);

  const can = useCallback(
    (permission: string) => user?.permissions.includes(permission) ?? false,
    [user],
  );

  const canAny = useCallback(
    (...permissions: string[]) => permissions.some((p) => can(p)),
    [can],
  );

  const value = useMemo(
    () => ({
      user,
      isLoading,
      // Keyed on the user alone. Also requiring an in-memory access token
      // would flap: the token is short-lived and briefly absent around a
      // refresh, but the session is still perfectly valid — apiFetch will
      // silently re-obtain it on the next call.
      isAuthenticated: user !== null,
      login,
      logout,
      can,
      canAny,
    }),
    [user, isLoading, login, logout, can, canAny],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
